import React from "react";
import { FormFieldProps } from "../../types/form-types";
import { useFormField } from "../../hooks/useFormField";
import { ChipGroup } from "./Chip";
import TagInput from "./TagInput";

const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  type = "text",
  placeholder,
  required = false,
  disabled = false,
  helperText,
  formContext,
  as = "input",
  options = [],
  rows = 4,
  style = {},
  autoComplete = "off",
  ...rest
}) => {
  const {
    value,
    error,
    inputStyle,
    labelStyle,
    containerStyle,
    errorStyle,
    helperStyle,
  } = useFormField({ name, formContext, disabled, style });

  const { handleChange, handleBlur } = formContext;

  const renderInput = () => {
    switch (as) {
      case "textarea":
        return (
          <textarea
            name={name}
            value={value}
            onChange={(e) => handleChange(name, e.target.value)}
            onBlur={() => handleBlur(name)}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            rows={rows}
            autoComplete={autoComplete}
            style={inputStyle}
            {...rest}
          />
        );

      case "select":
        return (
          <select
            name={name}
            value={value}
            onChange={(e) => handleChange(name, e.target.value)}
            onBlur={() => handleBlur(name)}
            disabled={disabled}
            required={required}
            autoComplete={autoComplete}
            style={inputStyle}
            {...rest}
          >
            <option value="">{placeholder || "Select an option"}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case "checkbox":
        return (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              name={name}
              checked={!!value}
              onChange={(e) => handleChange(name, e.target.checked)}
              onBlur={() => handleBlur(name)}
              disabled={disabled}
              autoComplete={autoComplete}
              style={{ width: "18px", height: "18px", cursor: "pointer" }}
              {...rest}
            />
            <span style={{ fontSize: "14px", color: "#374151" }}>{label}</span>
          </label>
        );

      case "chip":
        return (
          <ChipGroup
            options={options}
            value={value}
            onChange={(val) => handleChange(name, val)}
            multiple={rest.multiple}
            onAddCustom={rest.onAddCustom}
            disabled={disabled}
            style={inputStyle}
            chipStyle={rest.chipStyle}
            label={label}
            labelStyle={labelStyle}
            className={rest.className}
          />
        );

      case "tags":
        return (
          <TagInput
            label={label}
            labelStyle={labelStyle}
            value={value || []}
            onChange={(val) => handleChange(name, val)}
            disabled={disabled}
            helperText={helperText}
            containerStyle={inputStyle}
            onBlur={() => handleBlur(name)}
            placeholder={placeholder}
            {...rest}
          />
        );

      default:
        return (
          <input
            type={type}
            name={name}
            value={value}
            onChange={(e) => handleChange(name, e.target.value)}
            onBlur={() => handleBlur(name)}
            placeholder={placeholder}
            autoComplete={autoComplete}
            disabled={disabled} // Changed from: disabled={disabled}
            required={!!required} // Changed from: required={required} to fix boolean type issue
            style={inputStyle}
            {...rest}
          />
        );
    }
  };

  if (as === "checkbox" || as === "chip" || as === "tags") {
    return (
      <div style={containerStyle}>
        {renderInput()}
        {error && <p style={errorStyle}>{error}</p>}
        {/* {helperText && !error && <p style={helperStyle}>{helperText}</p>} */}
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {label && (
        <label htmlFor={name} style={labelStyle}>
          {label}
          {required && (
            <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
          )}
        </label>
      )}
      {renderInput()}
      {error && <p style={errorStyle}>{error}</p>}
      {helperText && !error && <p style={helperStyle}>{helperText}</p>}
    </div>
  );
};

export default FormField;
